import * as THREE from 'three';
import { getPerpendicularIntersection } from '../../../shared/geometry/getPrependicularIntersection';
import { createOffsetLine } from './createOffsetLine';
import { getActiveFloorPlanId, getGlobalDocumentBounds } from '../../../utils/getActiveFloorPlanId';

// ⚡ Aday-cache: "hangi çizgiler snap adayı" listesini ~200ms boyunca yeniden
// hesaplama. Her mouse hareketinde O(sahne) traverse + frustum + updateMatrixWorld
// yapmak yerine aday listesi cache'lenir; RAYCAST yine her mouse hareketinde çalışır
// (imleç-en-yakın-segment doğru kalır). En fazla ~200ms bayatlık: yeni konan wire
// kısa süre sonra snap'lenebilir hale gelir. scene/kat planı/tempLine değişince yenilenir.
const CAND_CACHE_MS = 200;
let _candCache = { scene: null, floorPlanId: null, tempLine: null, t: 0, candidates: null };

/**
 * Duvar + Wire snap handler
 * - Tüm Line / Polyline / Wire segmentlerini aday olarak toplar
 * - Mouse'a en yakın segment üzerinden offset çizgileri üretir
 */
export const handleSnapWall = ({
  scene,
  camera,
  mouseNDC,
  worldPoint,
  refs,
  floorPlans,          // redux'tan gelen kat planları
  wires = [],          // 👈 yeni parametre: redux wireSlice içindeki wire dataları
  floorPlanObjectIds,  // opsiyonel: sadece seçilen objelerin id listesi
}) => {
  const { intersectedLineRef, offsetLineRef, verticesRef, tempLineRef } = refs;

  const raycaster = new THREE.Raycaster();
  raycaster.params.Line.threshold = 5;
  raycaster.setFromCamera(mouseNDC, camera);

  // ✅ Aktif kat planı ID'sini tespit et (doküman bazlı filtreleme için)
  const documentBounds = getGlobalDocumentBounds();
  const activeFloorPlanId = worldPoint ? getActiveFloorPlanId(worldPoint, documentBounds) : null;

  // Aday listesini ~200ms cache'le (scene/kat planı/tempLine sabitken yeniden hesaplama)
  let candidates;
  const _tempLine = tempLineRef?.current || null;
  const _nowT = (typeof performance !== 'undefined' ? performance.now() : 0);
  if (
    _candCache.candidates &&
    _candCache.scene === scene &&
    _candCache.floorPlanId === activeFloorPlanId &&
    _candCache.tempLine === _tempLine &&
    (_nowT - _candCache.t) < CAND_CACHE_MS
  ) {
    candidates = _candCache.candidates;
  } else {
    // ⚡ Tek scene.traverse ile tüm Line'ları uuid→obj haritala.
    const lineByUuid = new Map();
    scene.traverse(o => { if (o.isLine) lineByUuid.set(o.uuid, o); });

    // ⚡ Viewport (frustum) culling — görüş alanına girmeyen adayları hiç işleme.
    camera.updateMatrixWorld();
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    const _frustum = new THREE.Frustum().setFromProjectionMatrix(
      new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
    );
    const _cullSphere = new THREE.Sphere();
    const isInView = (obj) => {
      obj.updateMatrixWorld(true);
      const geo = obj.geometry;
      if (!geo) return false;
      if (!geo.boundingSphere) geo.computeBoundingSphere();
      if (!geo.boundingSphere) return true;
      _cullSphere.copy(geo.boundingSphere).applyMatrix4(obj.matrixWorld);
      return _frustum.intersectsSphere(_cullSphere);
    };

    candidates = [];
    const addedIds = new Set();
    const tryAdd = (obj) => {
      if (!obj || addedIds.has(obj.uuid)) return;
      if (obj.userData?.isIntersectPreview || obj.userData?.isOffsetPreview) return;
      if (obj.userData?.role === 'wireMarker') return;
      if (_tempLine && obj === _tempLine) return;
      if (!isInView(obj)) return;
      obj.geometry?.computeBoundingBox();
      candidates.push(obj);
      addedIds.add(obj.uuid);
    };

    // Kat planı çizgileri (aktif kat, wire-like)
    floorPlans.forEach(fp => {
      if (activeFloorPlanId && fp.id !== activeFloorPlanId) return;
      if (!fp.objects || !Array.isArray(fp.objects)) return;
      fp.objects.forEach(obj => {
        if (obj.type !== 'Line') return;
        if (!['line', 'polyline', 'wire'].includes(obj.userData?.type)) return;
        tryAdd(obj);
      });
    });
    // Redux wire'ları → uuid haritasından O(1)
    wires.forEach(w => tryAdd(lineByUuid.get(w.sceneUUID)));
    // Panel / mainPanel hatları
    lineByUuid.forEach(obj => {
      const layer = obj.userData?.layer;
      const type = obj.userData?.type;
      const name = obj.name;
      const isPanelWire =
        layer === 'electricalPanel' || layer === 'simpleMainPanel' ||
        name === 'electricalPanelWire' || name === 'simpleMainPanelWire' ||
        name === 'mainPanelFeeder' || type === 'polyline';
      if (isPanelWire) tryAdd(obj);
    });

    _candCache = { scene, floorPlanId: activeFloorPlanId, tempLine: _tempLine, t: _nowT, candidates };
  }

  // 2) Eski offset çizgilerini temizle
  if (offsetLineRef.current) {
    const arr = Array.isArray(offsetLineRef.current)
      ? offsetLineRef.current
      : [offsetLineRef.current];
    arr.forEach(line => {
      scene.remove(line);
      line.geometry?.dispose?.();
      line.material?.dispose?.();
    });
    offsetLineRef.current = null;
  }

  // 3) Eski intersect çizgilerini temizle
  if (intersectedLineRef.current) {
    scene.remove(intersectedLineRef.current);
    intersectedLineRef.current.geometry?.dispose?.();
    intersectedLineRef.current.material?.dispose?.();
    intersectedLineRef.current = null;
  }

  const intersects = raycaster.intersectObjects(candidates, true);
  if (intersects.length === 0) return null;

  // 4) En yakın segmenti bul
  const intersected = intersects[0].object;
  const posAttr = intersected.geometry?.attributes?.position;
  if (!posAttr || posAttr.count < 2) return null;

  let targetSegment = null;
  let minDist = Infinity;

  for (let i = 0; i < posAttr.count - 1; i++) {
    const start = new THREE.Vector3().fromBufferAttribute(posAttr, i);
    const end = new THREE.Vector3().fromBufferAttribute(posAttr, i + 1);

    intersected.localToWorld(start);
    intersected.localToWorld(end);

    const closest = getPerpendicularIntersection(worldPoint, { start, end });
    const dist = closest.distanceTo(worldPoint);

    if (dist < minDist) {
      minDist = dist;
      targetSegment = { start, end };
    }
  }

  if (!targetSegment || verticesRef.current.length === 0) return null;

  const lastVertex = verticesRef.current[verticesRef.current.length - 1];

  // 5) Offset çizgilerini oluştur
  const invisibleSegment = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([targetSegment.start, targetSegment.end]),
    new THREE.LineBasicMaterial({ visible: false })
  );

  const magentaLine = createOffsetLine(invisibleSegment, lastVertex, 5, {
    color: 0xff00ff,
    dashed: true
  });
  const cyanLine = createOffsetLine(invisibleSegment, lastVertex, -5, {
    color: 0x00ccff,
    dashed: true
  });

  magentaLine.userData.isOffsetPreview = true;
  cyanLine.userData.isOffsetPreview = true;

  scene.add(magentaLine);
  scene.add(cyanLine);

  offsetLineRef.current = [magentaLine, cyanLine];

  return {
    intersected,
    targetSegment,
    offsetLines: [magentaLine, cyanLine]
  };
};
